import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import FittingRoomsAPI from "../../api/FittingRoomsAPI";
import MeetingsAPI from "../../api/MeetingsAPI";
import { useStoreCountry } from "../../hooks/useStoreCountry";
import { storeCountryToLanguageCode } from "../../shared/supported-languages";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import Switch from "../Switch/Switch";
import Button from "../Button/Button";
import ExpandableSection from "../ExpandableSection/ExpandableSection";
import PlainTextButton from "../PlainTextButton/PlainTextButton";
import { LabelV2 } from "../LabelV2/LabelV2";
import {
  expandRecurrence,
  type RecurrenceOptions,
  type DailyTimeRanges,
  WEEKDAYS,
  WEEKENDS,
} from "../../utils/blockedDatesRecurrence";
import { cn } from "../../lib/utils";

export interface BlockedDate {
  id?: number;
  start: string;
  end: string;
}

const DAY_LABELS: { key: string; value: number }[] = [
  { key: "calendar.blocked_dates.repeat.days.mon", value: 1 },
  { key: "calendar.blocked_dates.repeat.days.tue", value: 2 },
  { key: "calendar.blocked_dates.repeat.days.wed", value: 3 },
  { key: "calendar.blocked_dates.repeat.days.thu", value: 4 },
  { key: "calendar.blocked_dates.repeat.days.fri", value: 5 },
  { key: "calendar.blocked_dates.repeat.days.sat", value: 6 },
  { key: "calendar.blocked_dates.repeat.days.sun", value: 0 },
];

/** Parses "HH:mm" to a Date with that time (reference date 1970-01-01). */
function timeStringToDate(s: string): Date {
  const [h = 0, m = 0] = s.split(":").map(Number);
  const d = new Date(0);
  d.setHours(h, m, 0, 0);
  return d;
}

/** Formats a Date as "HH:mm" (time part only). */
function dateToTimeString(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export type BlockedDatesFormVariant = "global" | "single_fitting_room";

type Props = {
  defaultDate?: Date;
  fn?: () => void;
  timeZone?: string;
  /** "global" = blocked dates (no repeat); "single_fitting_room" = create meetings with repeat options */
  variant?: BlockedDatesFormVariant;
};

const BlockedDatesForm: React.FC<Props> = ({ defaultDate, fn = () => null, timeZone, variant = "global" }) => {
  const showRepeatSection = variant === "single_fitting_room";

  const [start, setStart] = useState(() => {
    const date = defaultDate || new Date();
    const s = new Date(date);
    s.setHours(0, 0, 0, 0);
    return s;
  });
  const [end, setEnd] = useState(() => {
    const date = defaultDate || new Date();
    const s = new Date(date);
    s.setHours(0, 0, 0, 0);
    if (variant === "single_fitting_room") {
      const endOfMonth = new Date(s.getFullYear(), s.getMonth() + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);
      return endOfMonth;
    }
    const e = new Date(date);
    e.setHours(23, 59, 59, 999);
    return e;
  });
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [blockAllDay, setBlockAllDay] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [weeklyDays, setWeeklyDays] = useState<number[]>([...WEEKDAYS]);
  /** Optional start/end time-of-day per weekday (0–6). Absent = full day. */
  const [weeklyDayTimes, setWeeklyDayTimes] = useState<DailyTimeRanges>({});
  const [showCustomizeDaysHours, setShowCustomizeDaysHours] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { t, i18n } = useTranslation();
  const storeCountry = useStoreCountry();
  const titleLanguage = storeCountryToLanguageCode(storeCountry) || i18n.language;

  // Only adjust times when switching between all day and hourly modes
  const handleBlockTypeChange = (newBlockAllDay: boolean) => {
    if (newBlockAllDay) {
      let adjustedStart = new Date(start);
      adjustedStart.setHours(0, 0, 0, 0);
      let adjustedEnd = new Date(end);
      adjustedEnd.setHours(23, 59, 59, 999);

      setStart(adjustedStart);
      setEnd(adjustedEnd);
    } else {
      // When switching to hourly blocking, set start time to 9 AM
      let adjustedStart = new Date(start);
      adjustedStart.setHours(9, 0, 0, 0);
      let adjustedEnd = new Date(end);
      adjustedEnd.setHours(17, 0, 0, 0);

      setStart(adjustedStart);
      setEnd(adjustedEnd);
    }
    setBlockAllDay(newBlockAllDay);
  };

  // Handle date changes in full day mode (or single_fitting_room: date-only range)
  const handleDateChange = (newDate: Date, isStart: boolean) => {
    if (blockAllDay || showRepeatSection) {
      const adjustedDate = new Date(newDate);
      if (isStart) {
        adjustedDate.setHours(0, 0, 0, 0);
        setStart(adjustedDate);
        
        // If start date is after end date, update end date to match start date
        const currentEnd = new Date(end);
        currentEnd.setHours(0, 0, 0, 0);
        if (adjustedDate > currentEnd) {
          const newEnd = new Date(adjustedDate);
          newEnd.setHours(23, 59, 59, 999);
          setEnd(newEnd);
        }
      } else {
        adjustedDate.setHours(23, 59, 59, 999);
        setEnd(adjustedDate);
      }
    } else {
      if (isStart) {
        setStart(newDate);
      } else {
        setEnd(newDate);
      }
    }
  };

  const recurrenceOptions: RecurrenceOptions = useMemo(
    () =>
      showRepeatSection
        ? {
            repeatType: "weekly" as const,
            weeklyDays,
            endCondition: "until_date" as const,
            repeatUntilDate: end,
          }
        : {
            repeatType: "none" as const,
            weeklyDays: [],
            endCondition: "until_date" as const,
            repeatUntilDate: undefined,
          },
    [showRepeatSection, weeklyDays, end]
  );

  const weeklyDaysInvalid = showRepeatSection && weeklyDays.length === 0;
  const untilDateInvalid =
    showRepeatSection &&
    (() => {
      const startDate = new Date(start);
      const endDate = new Date(end);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      return endDate < startDate;
    })();

  useEffect(() => {
    setSubmitError(null);
  }, [start, end, weeklyDays]);

  useEffect(() => {
    if (blockAllDay || showRepeatSection) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      setIsButtonDisabled(endDate < startDate);
    } else {
      setIsButtonDisabled(end <= start);
    }
  }, [start, end, blockAllDay, showRepeatSection]);

  const setDayChecked = (day: number, checked: boolean) => {
    if (checked) {
      setWeeklyDays((prev) => [...prev, day].sort((a, b) => a - b));
    } else {
      setWeeklyDays((prev) => prev.filter((d) => d !== day));
      setWeeklyDayTimes((t) => {
        const next = { ...t };
        delete next[day];
        return next;
      });
    }
  };

  const setWeekdaysPreset = () => setWeeklyDays(() => [1, 2, 3, 4, 5]);
  const setWeekendsPreset = () => setWeeklyDays(() => [0, 6]);

  const sortedWeeklyDays = useMemo(() => [...weeklyDays].sort((a, b) => a - b), [weeklyDays]);
  const isWeekdaysSelected =
    sortedWeeklyDays.length === WEEKDAYS.length &&
    WEEKDAYS.every((d, i) => d === sortedWeeklyDays[i]);
  const isWeekendsSelected =
    sortedWeeklyDays.length === WEEKENDS.length &&
    WEEKENDS.every((d, i) => d === sortedWeeklyDays[i]);

  const setWeeklyDayTimeRange = (day: number, from: string, to: string) => {
    if (!from && !to) {
      setWeeklyDayTimes((prev) => {
        const next = { ...prev };
        delete next[day];
        return next;
      });
      return;
    }
    const start = from ? timeStringToDate(from) : new Date(0);
    if (!from) start.setHours(0, 0, 0, 0);
    const end = to ? timeStringToDate(to) : new Date(0);
    if (!to) end.setHours(23, 59, 59, 999);
    setWeeklyDayTimes((prev) => ({ ...prev, [day]: { start, end } }));
  };

  const blockDate = async () => {
    setIsSubmitting(true);
    try {
      if (variant === "global") {
        await MeetingsAPI.block({
          start: start.toISOString(),
          end: end.toISOString(),
        });
      } else {
        const rangeEnd = new Date(end);
        rangeEnd.setHours(23, 59, 59, 999);
        const occurrences = expandRecurrence(start, rangeEnd, true, recurrenceOptions, weeklyDayTimes);
        if (occurrences.length === 0) {
          setSubmitError(t("calendar.blocked_dates.repeat.no_occurrences_in_range"));
          return;
        }
        setSubmitError(null);
        const titleLabel = t("calendar.blocked_dates.single_fitting_room.meeting_title", { lng: titleLanguage }) ?? "Fitting room blocked";
        for (const occ of occurrences) {
          await FittingRoomsAPI.block({
            startTime: occ.start.toISOString(),
            endTime: occ.end.toISOString(),
            title: titleLabel,
          });
        }
      }
      setSubmitError(null);
      fn();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="mt-0 mb-6 p-3 bg-blue-50 rounded-md border border-blue-100">
        <div className="flex items-start space-x-2">
          <div className="text-blue-500 mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <div>
            <p className="text-sm text-blue-700">
              {variant === "single_fitting_room"
                ? (t("calendar.blocked_dates.single_fitting_room.info_note", {
                    defaultValue: "This creates placeholder meetings that block one fitting room. Use this when you have fewer fitting rooms available on certain days (e.g. weekdays). You can repeat the block by day or week."
                  }) as string)
                : t("calendar.blocked_dates.form.info_note", {
                    defaultValue: "Blocked periods will prevent bookings from being made during these times in your calendar. You can block full days or specific hours as needed (holidays, special events, or other reasons)."
                  })}
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-6">
        {!showRepeatSection && (
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t("calendar.blocked_dates.form.all_day")}</span>
              <Switch
                checked={blockAllDay}
                onChange={() => handleBlockTypeChange(!blockAllDay)}
              />
            </div>
          </div>
        )}

        <div className="flex space-x-5">
          <CustomDatePicker
            label="calendar.blocked_dates.form.start"
            value={start}
            onChange={(c) => handleDateChange(c, true)}
            showTimeSelect={showRepeatSection ? false : !blockAllDay}
            dateFormat={showRepeatSection || blockAllDay ? "yyyy-MM-dd" : "yyyy-MM-dd HH:mm"}
            timeZone={timeZone}
          />
          <CustomDatePicker
            label="calendar.blocked_dates.form.end"
            value={end}
            onChange={(c) => handleDateChange(c, false)}
            showTimeSelect={showRepeatSection ? false : !blockAllDay}
            dateFormat={showRepeatSection || blockAllDay ? "yyyy-MM-dd" : "yyyy-MM-dd HH:mm"}
            timeZone={timeZone}
          />
        </div>

        {/* Single fitting room: only days of the week */}
        {showRepeatSection && (
          <div className="space-y-3 border-t border-gray-200 pt-4">
            <div>
              <LabelV2 className="font-medium text-gray-700">
                {t("calendar.blocked_dates.single_fitting_room.days_and_hours_label")}
              </LabelV2>
              <p className="text-xs text-gray-500 mt-1">
                {t("calendar.blocked_dates.single_fitting_room.days_and_hours_hint")}
              </p>
            </div>
            <div
              className="flex flex-wrap items-center gap-2"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Button
                text={t("calendar.blocked_dates.repeat.weekdays") ?? "Weekdays"}
                fn={setWeekdaysPreset}
                cssStyles={cn(
                  "text-xs px-2.5 py-1 rounded-full font-medium border",
                  isWeekdaysSelected
                    ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                    : "text-primary border-gray-300 bg-white hover:bg-gray-50"
                )}
              />
              <Button
                text={t("calendar.blocked_dates.repeat.weekends") ?? "Weekends"}
                fn={setWeekendsPreset}
                cssStyles={cn(
                  "text-xs px-2.5 py-1 rounded-full font-medium border",
                  isWeekendsSelected
                    ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                    : "text-primary border-gray-300 bg-white hover:bg-gray-50"
                )}
              />
            </div>
            <ExpandableSection
              defaultState={showCustomizeDaysHours}
              collapsedContent={
                <div className="inline-flex">
                  <PlainTextButton
                    text={t("calendar.blocked_dates.single_fitting_room.customize_days_hours")}
                    onClick={() => setShowCustomizeDaysHours((prev) => !prev)}
                    actionName="Expand Customize days and hours"
                  />
                </div>
              }
              expandedContent={
                <div id="expandable-content" className="space-y-4 p-3 border rounded-lg bg-gray-50 mt-2">
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1 px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-600">
                      <span>{t("calendar.blocked_dates.repeat.day_column")}</span>
                      <span className="min-w-[4.5rem]">{t("calendar.blocked_dates.single_fitting_room.from")}</span>
                      <span className="min-w-[4.5rem]">{t("calendar.blocked_dates.single_fitting_room.to")}</span>
                    </div>
                    {DAY_LABELS.map(({ key, value }) => {
                      const checked = weeklyDays.includes(value);
                      const dayTimes = weeklyDayTimes[value];
                      return (
                        <div
                          key={value}
                          className={cn(
                            "grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1 px-3 py-2 items-center border-b border-gray-100 last:border-b-0",
                            !checked && "opacity-60"
                          )}
                        >
                          <div className="min-w-0 flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`blocked-day-${value}`}
                              checked={checked}
                              onChange={(e) => setDayChecked(value, e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                            />
                            <label htmlFor={`blocked-day-${value}`} className="cursor-pointer text-sm">
                              {t(key)}
                            </label>
                          </div>
                          <input
                            type="time"
                            className="rounded border border-gray-300 px-2 py-1.5 text-sm w-full min-w-0 focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50 disabled:text-gray-400"
                            value={dayTimes ? dateToTimeString(dayTimes.start) : ""}
                            disabled={!checked}
                            onChange={(e) => {
                              const from = e.target.value;
                              const to = dayTimes ? dateToTimeString(dayTimes.end) : "";
                              setWeeklyDayTimeRange(value, from, to);
                            }}
                            aria-label={`${t(key)} ${t("calendar.blocked_dates.single_fitting_room.from")}`}
                          />
                          <input
                            type="time"
                            className="rounded border border-gray-300 px-2 py-1.5 text-sm w-full min-w-0 focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-50 disabled:text-gray-400"
                            value={dayTimes ? dateToTimeString(dayTimes.end) : ""}
                            disabled={!checked}
                            onChange={(e) => {
                              const to = e.target.value;
                              const from = dayTimes ? dateToTimeString(dayTimes.start) : "";
                              setWeeklyDayTimeRange(value, from, to);
                            }}
                            aria-label={`${t(key)} ${t("calendar.blocked_dates.single_fitting_room.to")}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              }
            />
            {weeklyDaysInvalid && (
              <p className="text-sm text-destructive">{t("calendar.blocked_dates.repeat.select_at_least_one_day")}</p>
            )}
          </div>
        )}

        {submitError && (
          <p className="text-sm text-destructive text-center">{submitError}</p>
        )}
        <div className="flex justify-center">
          <PrimaryButton 
            text={isSubmitting ? t("calendar.blocked_dates.form.confirm_creating")! : t("calendar.blocked_dates.form.confirm")!} 
            fn={blockDate} 
            isDisabled={isButtonDisabled || (showRepeatSection && (weeklyDaysInvalid || untilDateInvalid)) || isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

export default BlockedDatesForm;
